import FinancingType from '@carvana/merch-contracts/FinancingType';
import {
  calculateGetItBy,
  calculateSoonestAvailable
} from '@carvana/merch-tile';
import cx from 'classnames';
import { useContext, useMemo } from 'react';
import Skeleton from '@/common/helios/Skeleton';
import Image from '@/common/Image';
import TransitInfo from '@/common/TransitInfo';
import TransitChargeState from '~/constants/common/TransitChargeState';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import { usePriceBreakdownContext } from '~/context/new-cars/PriceBreakdownContext';
import type {
  CustomizeClosestVehicle,
  CustomizeItem
} from '~/contracts/new-cars';
import useClosestVehicle from '~/hooks/new-cars/selectors/useClosestVehicle';
import type { TransitChargeInfo } from '~/types/TransitChargeInfo';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import resolveIncentives from '~/utilities/incentives/resolveIncentives';
import optimizeImage from '~/utilities/optimizeImage';

function buildOptionItems({
  driveType,
  engine,
  exteriorColor,
  interiorColor,
  packageOptions
}: CustomizeClosestVehicle) {
  return [
    { label: `${exteriorColor.name} exterior paint`, ...exteriorColor },
    { label: `${interiorColor.name} interior`, ...interiorColor },
    driveType && { label: `${driveType.name} drivetrain`, ...driveType },
    engine && { label: engine.name, ...engine },
    ...packageOptions.map(p => ({ label: p.name, ...p }))
  ].filter((item): item is CustomizeItem & { label: string } =>
    Boolean(item?.code)
  );
}

export default function PriceBreakdownContent() {
  const { customizeOptions, selectedTrim, selectedTruckBodyStyle } =
    useContext(ParentModelContext);
  const {
    delivery,
    isPricingLoaded: isLoaded,
    isPricingLoading: isLoading,
    pickup,
    pricingData
  } = usePriceBreakdownContext();
  const closestVehicle = useClosestVehicle();
  const optionItems = useMemo(
    () => (closestVehicle ? buildOptionItems(closestVehicle) : []),
    [closestVehicle]
  );

  if (!closestVehicle) return null;

  const { imageUrl, make, model, year } = customizeOptions.data;
  const {
    appliedIncentives,
    appliedIncentivesAmount,
    incentivizedPrice = 0,
    marketplaceIncentives,
    msrp = 0
  } = closestVehicle;
  const optimizedImage = optimizeImage(imageUrl, {
    width: 450
  });

  const hasSoftPull =
    pricingData.financeInfo?.financingType === FinancingType.UserFinancing;
  const cashDown = pricingData.price?.downPaymentDetails?.additionalCashDown;
  const monthlyPayment = pricingData.price?.monthlyPayment;
  const shippingFee =
    pricingData.price?.downPaymentDetails?.nonRefundableShippingFees;

  const soonestAvailable = calculateSoonestAvailable({ delivery, pickup });
  const { getItBy } = calculateGetItBy(soonestAvailable, {
    ordinalDay: false,
    sentenceCase: true,
    truncateDay: true
  });
  const [getItByDay, getItByDate] = getItBy.split(',');
  const soonestDays = Number(soonestAvailable.daysToAvailability);

  const transitCharge: TransitChargeInfo = {
    charge: shippingFee ?? 0,
    isLoaded,
    isLoading,
    state:
      !isLoaded || shippingFee === undefined
        ? TransitChargeState.Unknown
        : shippingFee > 0
        ? TransitChargeState.HasCharge
        : TransitChargeState.NoCharge
  };

  const totalAppliedIncentives = resolveIncentives(
    appliedIncentives ?? [],
    marketplaceIncentives ?? []
  );
  const trimPriceAdd = selectedTrim?.priceAdd ?? 0;
  const truckBodyStylePriceAdd = selectedTruckBodyStyle?.priceAdd ?? 0;
  const sumOfOptionAdds = optionItems.reduce(
    (sum, item) => sum + item.priceAdd,
    0
  );
  const baseModelPrice =
    incentivizedPrice -
    trimPriceAdd -
    truckBodyStylePriceAdd -
    sumOfOptionAdds +
    appliedIncentivesAmount;

  const vehicleName = [year, make, model].filter(Boolean).join(' ');

  return (
    <>
      {optimizedImage && (
        <div className="overflow-hidden rounded-md">
          <Image
            alt={vehicleName}
            className="h-[156px] sm:h-[225px] w-full object-cover"
            loading="eager"
            src={optimizedImage}
          />
        </div>
      )}

      <p className="t-heading-lg text-text-strong">{vehicleName}</p>

      {/* Base vehicle + Trim */}
      <div className="flex flex-col gap-sm">
        {closestVehicle.baseModel && (
          <div className="flex items-center justify-between gap-xl">
            <p className="t-body-sm text-text-default">Base vehicle</p>
            <p className="t-strong-sm whitespace-nowrap text-text-strong">
              ${formatAndDropDecimal(baseModelPrice)}
            </p>
          </div>
        )}
        {closestVehicle.trim && (
          <div className="flex items-center justify-between gap-xl">
            <p className="t-body-sm text-text-default">
              Trim: {closestVehicle.trim}
            </p>
            <p className="t-strong-sm whitespace-nowrap text-text-strong">
              {trimPriceAdd === 0
                ? '+$0'
                : `+$${formatAndDropDecimal(trimPriceAdd)}`}
            </p>
          </div>
        )}
        {selectedTruckBodyStyle && (
          <div className="flex items-center justify-between gap-xl">
            <p className="t-body-sm text-text-default">
              {selectedTruckBodyStyle.title}
            </p>
            <p className="t-strong-sm whitespace-nowrap text-text-strong">
              {truckBodyStylePriceAdd === 0
                ? '+$0'
                : `+$${formatAndDropDecimal(truckBodyStylePriceAdd)}`}
            </p>
          </div>
        )}
      </div>

      {/* Colors & options */}
      {optionItems.length > 0 && (
        <div className="flex flex-col gap-sm border-t border-border-subtle pt-lg">
          <p className="t-strong-sm text-text-strong">Colors & options</p>
          {optionItems.map(({ label, priceAdd }) => (
            <div
              key={label}
              className="flex items-center justify-between gap-xl"
            >
              <p className="t-body-sm text-text-default">{label}</p>
              <p className="t-strong-sm whitespace-nowrap text-text-strong">
                {priceAdd === 0 ? '+$0' : `+$${formatAndDropDecimal(priceAdd)}`}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Incentive */}
      {totalAppliedIncentives.length > 0 && (
        <div className="flex flex-col gap-sm">
          <p className="t-strong-sm text-text-strong">Incentive</p>
          {totalAppliedIncentives.map(({ amount, programId, programName }) => (
            <div
              key={programId}
              className="flex items-center justify-between gap-xl"
            >
              <p className="t-body-sm text-text-default">{programName}</p>
              <p className="t-strong-sm whitespace-nowrap text-text-success">
                -${formatAndDropDecimal(amount)}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* MSRP */}
      <div className="flex items-center justify-between gap-xl border-t-2 border-border-strong pt-lg">
        <div className="flex items-center gap-sm">
          {msrp && (
            <>
              <p className="t-body-sm text-text-default">MSRP</p>
              <p className="t-body-sm text-text-disabled">
                <s>${formatAndDropDecimal(msrp)}</s>
              </p>
            </>
          )}
        </div>
        {incentivizedPrice && (
          <p className="t-heading-md whitespace-nowrap text-text-strong">
            ${formatAndDropDecimal(incentivizedPrice)}
          </p>
        )}
      </div>

      {/* Monthly payment + shipping */}
      <div
        aria-busy={!isLoaded || isLoading}
        className="flex flex-col gap-sm border-t border-border-subtle pt-lg"
      >
        {!isLoaded || isLoading ? (
          <Skeleton height={16} />
        ) : (
          monthlyPayment !== undefined && (
            <p className="t-body-sm text-text-default">
              <span
                className={cx({
                  't-strong-sm text-text-default': hasSoftPull
                })}
              >
                ${monthlyPayment.toLocaleString()}/mo
              </span>
              {!hasSoftPull && ' est'}
              {cashDown !== undefined && (
                <>
                  <span aria-hidden="true"> · </span>
                  <span
                    className={cx({
                      'text-text-success t-label-sm':
                        cashDown === 0 && hasSoftPull
                    })}
                  >
                    ${formatAndDropDecimal(cashDown)} cash down
                  </span>
                </>
              )}
            </p>
          )
        )}
        {(transitCharge.state !== TransitChargeState.Unknown || getItBy) && (
          <p className="t-body-sm text-text-default">
            {transitCharge.state !== TransitChargeState.Unknown && (
              <TransitInfo transitCharge={transitCharge} />
            )}
            {transitCharge.state !== TransitChargeState.Unknown && getItBy && (
              <span aria-hidden="true"> · </span>
            )}
            {getItBy && (
              <span
                className={cx('t-label-sm text-text-strong', {
                  '!font-semibold': soonestDays < 4
                })}
              >
                Get it {getItByDay}
                {getItByDate ? `, ${getItByDate}` : ''}
              </span>
            )}
          </p>
        )}
      </div>
    </>
  );
}
